/**
 * Перенос трёх файлов Dart в один модуль:
 * `domain/models/versions_models/version_model/version_model.dart`,
 * `domain/models/versions_models/full_version_model/full_version_model.dart`
 * и `utils/serializers/version_model_serializer.dart`.
 *
 * Сериализатор в Dart существовал только ради `@JsonConverter` (freezed
 * требовал класс) — в TypeScript это пара обычных функций, отдельный файл
 * ей не нужен.
 */

import { z } from 'zod';

/** Строка версии всегда `major.minor.patch` — ровно три части. */
const VERSION_PARTS_COUNT = 3;

/** Целое число со знаком: `int.parse` в Dart другого не принимает. */
const INTEGER_PATTERN = /^-?\d+$/;

export interface VersionModel {
  readonly major: number;
  readonly minor: number;
  readonly patch: number;
}

/**
 * Аналог `int.parse`: `Number.parseInt` молча вернул бы `NaN` на мусоре,
 * а Dart бросал `FormatException` — ошибку глотать нельзя.
 */
function parseIntStrict(source: string): number {
  if (!INTEGER_PATTERN.test(source)) {
    throw new Error(`FormatException: Invalid radix-10 number - "${source}"`);
  }
  return Number(source);
}

/** `VersionModelSerializer.fromJson` / `VersionModel.fromString`. */
export function versionModelFromString(value: string): VersionModel {
  const versionList = value.split('.');
  if (versionList.length !== VERSION_PARTS_COUNT) {
    throw new Error(
      `Version string must contain 3 digits x.x.x, currenly have - ${versionList.length}`,
    );
  }

  const major = parseIntStrict(versionList[0]);
  const minor = parseIntStrict(versionList[1]);
  const patch = parseIntStrict(versionList[2]);
  return { major, minor, patch };
}

/** `VersionModelSerializer.toJson`, он же `VersionModel.toString()`. */
export function formatVersion(version: VersionModel): string {
  const major = version.major;
  const minor = version.minor;
  const patch = version.patch;
  return `${major}.${minor}.${patch}`;
}

/**
 * Перенос `operator >` из Dart в виде обычного компаратора: сравнение идёт
 * по старшинству major → minor → patch, как в оригинальной лестнице `if`.
 */
export function compareVersions(a: VersionModel, b: VersionModel): number {
  if (a.major !== b.major) return a.major - b.major;
  if (a.minor !== b.minor) return a.minor - b.minor;
  return a.patch - b.patch;
}

/** `serverVersion > currentVersion` из `AppState.updateRequired`. */
export function versionGreaterThan(a: VersionModel, b: VersionModel): boolean {
  return compareVersions(a, b) > 0;
}

/** `@VersionModelSerializer()` — в JSON версия лежит строкой `x.x.x`. */
export const versionModelSchema = z.string().transform((value): VersionModel => {
  return versionModelFromString(value);
});

export interface FullVersionModel {
  readonly appVersion: VersionModel;
  readonly versionCode: number;
}

/**
 * Ключи приходят в snake_case: схема разбирает их «как есть», а `transform`
 * переводит в camelCase. Этот же приём используется во всех моделях порта.
 */
const fullVersionModelRawSchema = z.object({
  app_version: versionModelSchema,
  version_code: z.number(),
});

export const fullVersionModelSchema = fullVersionModelRawSchema.transform(
  (raw): FullVersionModel => ({
    appVersion: raw.app_version,
    // `(json['version_code'] as num).toInt()` — Dart отбрасывает дробную часть.
    versionCode: Math.trunc(raw.version_code),
  }),
);

export function fullVersionModelFromJson(json: unknown): FullVersionModel {
  return fullVersionModelSchema.parse(json);
}

export function fullVersionModelToJson(model: FullVersionModel): Record<string, unknown> {
  return {
    app_version: formatVersion(model.appVersion),
    version_code: model.versionCode,
  };
}

/**
 * Замена `FullVersionModel.fromPackageInfo`: package_info_plus вместе с
 * остальной Android-частью из порта убран, версия и номер сборки приходят
 * от Vite (`__APP_VERSION__`, `__BUILD_NUMBER__`). Разбор тот же:
 * `VersionModel.fromString(info.version)` и `int.parse(info.buildNumber)`.
 */
export function fullVersionModelFromBuild(
  version: string,
  buildNumber: string,
): FullVersionModel {
  return {
    appVersion: versionModelFromString(version),
    versionCode: parseIntStrict(buildNumber),
  };
}
